import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters } from 'store/useBusinessToolStore/selectors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { ReadOnlyNumberInput } from 'components/_basic/Form/NumberInput/ReadOnlyNumberInput';
import { Loader } from 'components/_basic/Loader/Loader';

import { getCurrencySymbol } from 'helpers/getCurrencySymbol';
import { concatWithDot } from 'helpers/string';

import { IEachColumn, IInvestmentGridColumnsProps } from './types';
import { useInvestmentRatiosLoading } from '../../hooks/useInvestmentRatiosLoading';
import { columns, firstColumnValues, secondColumnValues } from '../../utils/form';
import { columnWidth } from '../constants';

import styles from './InvestmentGridColumns.module.scss';

const EachColumn: FC<IEachColumn> = ({ rowIndex, name, isLoading }) => {
  const { t } = useTranslation();
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const { currency, unit } = businessPlanChapters.GLOBAL;
  const currencySymbol = getCurrencySymbol(currency);

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.FINANCIAL_PERFORMANCE,
  );

  return (
    <>
      {columns.map((column, index) => {
        const key = `${column}${rowIndex}`;
        const width = columnWidth[index];
        const titleGridClass = styles.titleGrid;

        if (index === 0) {
          return (
            <Grid item key={key} className={titleGridClass}>
              <Typography
                variant="subtitle2"
                className={clsx(styles.title, { [styles.disabled]: !canAccess })}
                sx={{ width }}
              >
                {t(`financialPlan.financialPerformance.investment.columns.rowNames.${firstColumnValues[rowIndex]}`)}
              </Typography>
            </Grid>
          );
        }

        if (index === 1) {
          return (
            <Grid item key={key} className={titleGridClass}>
              <Typography variant="subtitle1" sx={{ width }} className={clsx({ [styles.disabled]: !canAccess })}>
                {secondColumnValues[rowIndex] === 'npv'
                  ? `${currencySymbol} ${unit !== 'single' ? t(`pdfPages.mappings.financialPlan.dimensionality.${unit}`) : ''}`
                  : t(`financialPlan.financialPerformance.investment.columns.rowNames.${secondColumnValues[rowIndex]}`)}
              </Typography>
            </Grid>
          );
        }

        return (
          <Grid item key={key} width={'100%'} sx={{ minWidth: width }} className={styles.inputCell}>
            {isLoading && <Loader size={20} className={styles.cellLoader} />}
            <ReadOnlyNumberInput
              id={name}
              name={name}
              disabled={!canAccess}
              decimalScale={1}
              className={clsx(styles.rowInput, { [styles.hiddenValue]: isLoading })}
              allowNegative
            />
          </Grid>
        );
      })}
    </>
  );
};

export const InvestmentGridColumns: FC<IInvestmentGridColumnsProps> = ({ name }) => {
  const isLoading = useInvestmentRatiosLoading();

  return (
    <Grid container display="flex" gap={1} className={styles.container}>
      {firstColumnValues.map((item, index) => {
        const fieldName = concatWithDot(name, item);

        return (
          <Grid item xs="auto" display={'flex'} gap={'8px'} key={index}>
            <EachColumn rowIndex={index} name={fieldName} isLoading={isLoading} />
          </Grid>
        );
      })}
    </Grid>
  );
};
